<template>
    <ShowTemplate @reloadAffiliationPanel="reloadAffiliationPanel" :model="organization"
        :owners="[organization.organization].filter((owner) => owner != null)"
        :otherOwner="organization.organizationable" :otherOwnerTitle="$lang.get('Belongs to')"
        :descriptionTabs="descriptionTabs" :access="organization.access" :can="permissions" :rolePanels="rolePanels"
        :availableEmotions="availableEmotions" :strippedView="!canAccess" :relations="relations"
        :statistics="statistics" :affiliationPanels="affiliationPanels" :isInQueue="isInQueue">
        <template #imageSlot>
            <CoverPicture modelClass="organization" :coverPictureId="organization.mediaIds.length >= 2
                ? organization.mediaIds[1]
                : null
                " :detailPictureId="organization.mediaIds.length >= 1
                    ? organization.mediaIds[0]
                    : null
                    " />
        </template>
        <template #tagSlot>
            <PartnerTag v-if="isPartner" />
        </template>
        <template #toolRowSlot v-if="canRequestToJoin || hasRequestedToJoin || hasBeenInvited">
            <Link v-if="hasBeenInvited && acceptInvitationUrl" :href="acceptInvitationUrl" method="post">
            <BaseButton size="lg" class="flex items-center gap-x-2"
                colors="bg-organization-accent hover:bg-organization-accent-hover text-organization whitespace-nowrap">
                <PhCheck class="size-6" />
                <span v-text="$lang.get('Accept invitation')" />

                <TextTooltip :text="$lang.get(
                    'You have recieved an invitation to join :model',
                    {
                        model: organization.name,
                    }
                )
                    ">
                    <PhQuestion
                        class="size-5 hover:text-organization-hover transition-colors duration-button-hover cursor-help"
                        weight="fill" />
                </TextTooltip>
            </BaseButton>
            </Link>
            <BaseButton v-else-if="hasRequestedToJoin" size="xl" class="flex items-center gap-x-1"
                colors="border border-organization-accent text-organization-accent cursor-default">
                <PhCheck class="size-6" />
                <span v-text="$lang.get('Request sent')" />

                <TextTooltip :text="$lang.get(
                    'A membership request has been sent to :definitive_organization. You will be notified when your request is accepted.',
                    {
                        definitive_organization:
                            this.$getBaseModelTranslation({
                                modelName: 'organization',
                                wordClass: 'definitive',
                            }),
                    }
                )
                    ">
                    <PhQuestion
                        class="size-5 hover:text-organization-accent-hover transition-colors duration-button-hover cursor-help"
                        weight="fill" />
                </TextTooltip>
            </BaseButton>

            <Link v-else-if="canRequestToJoin" :href="route(
                'organization.membership.request-to-join',
                organization.id
            )
                ">
            <BaseButton size="lg" class="flex items-center gap-x-2"
                colors="bg-organization-accent hover:bg-organization-accent-hover text-organization whitespace-nowrap">
                <PhSignIn class="size-6" />
                <span v-text="$lang.get('Request to join')" />

                <TextTooltip :text="$lang.get(
                    'Send a membership request to the administrators of :definitive_organization',
                    {
                        definitive_organization:
                            this.$getBaseModelTranslation({
                                modelName: 'organization',
                                wordClass: 'definitive',
                            }),
                    }
                )
                    ">
                    <PhQuestion
                        class="size-5 hover:text-organization-hover transition-colors duration-button-hover cursor-help"
                        weight="fill" />
                </TextTooltip>
            </BaseButton>
            </Link>
        </template>
    </ShowTemplate>
</template>

<script>
import ShowTemplate from '@/Templates/ShowTemplate.vue'
import CoverPicture from '@/Templates/ShowTemplateComponents/CoverPicture.vue'
import MemberPanel from '@/Components/Panels/AffiliationPanel/MemberPanel.vue'
import EmployeePanel from '@/Components/Panels/AffiliationPanel/EmployeePanel.vue'
import { router, Link } from '@inertiajs/vue3'
import BaseButton from '@/Components/Elements/BaseButton.vue'
import { PhSignIn, PhQuestion, PhCheck } from '@phosphor-icons/vue'
import TextTooltip from '@/Components/Elements/TextTooltip.vue'
import PartnerTag from '@/Components/Elements/Tags/PartnerTag.vue'

export default {
    components: {
        ShowTemplate,
        CoverPicture,
        MemberPanel,
        EmployeePanel,
        BaseButton,
        PhSignIn,
        PhQuestion,
        PhCheck,
        TextTooltip,
        Link,
        PartnerTag,
    },

    props: {
        organization: {
            type: Object,
            required: true,
        },
        permissions: {
            type: Object,
            required: true,
        },
        rolePanels: {
            type: Array,
            required: true,
        },
        availableEmotions: {
            type: Array,
            required: true,
        },
        canAccess: {
            type: Boolean,
            required: true,
        },
        canRequestToJoin: {
            type: Boolean,
            required: true,
        },
        hasRequestedToJoin: {
            type: Boolean,
            required: true,
        },
        hasBeenInvited: {
            type: Boolean,
            required: true,
        },
        acceptInvitationUrl: {
            type: String,
            required: false,
        },
        events: {
            type: Object,
            default: {},
        },
        learnings: {
            type: Object,
            default: {},
        },
        products: {
            type: Object,
            default: {},
        },
        resources: {
            type: Object,
            default: {},
        },
        usecases: {
            type: Object,
            default: {},
        },
        organizations: {
            type: Object,
            default: {},
        },
        statistics: {
            type: Object,
            default: {},
        },
        employees: {
            type: Array,
            default: [],
        },
        members: {
            type: Array,
            default: [],
        },
        pendingMembershipInvitations: {
            type: Array,
            default: [],
        },
        pendingMembershipRequests: {
            type: Array,
            default: [],
        },
        pendingEmployeeRequests: {
            type: Array,
            default: [],
        },
        canHaveMembers: {
            type: Boolean,
            required: true,
        },
        isPartner: {
            type: Boolean,
            required: true,
        },
        isInQueue: {
            type: Boolean,
            default: false
        },
        queryParams: {
            type: Object,
            default: () => ({}),
        },

    },

    mounted() { },

    computed: {
        descriptionTabs() {
            return [
                {
                    id: 'description',
                    name: this.$lang.get('Description'),
                    content: this.organization.description,
                },
            ].filter((tab) => !!tab.content)
        },

        relations() {
            return [
                this.resources,
                this.events,
                this.products,
                this.learnings,
                this.usecases,
                this.organizations,
            ].filter((relation) => Object.keys(relation).length > 0)
        },

        affiliationPanels() {
            let panels = []

            if (this.canHaveMembers) {
                panels.push({
                    id: 'members',
                    component: MemberPanel,
                    parentModel: this.organization,
                    items: this.members,
                    sentRequests: this.pendingMembershipInvitations,
                    recievedRequests: this.pendingMembershipRequests,
                    openOnLoad: Object.hasOwn(this.queryParams, 'memberpanel'),
                    activeTab: Object.hasOwn(this.queryParams, 'memberpanel')
                        ? this.queryParams.memberpanel
                        : null,
                })
            } else {
                panels.push({
                    id: 'employeepanel',
                    component: EmployeePanel,
                    parentModel: this.organization,
                    items: this.employees,
                    recievedRequests: this.pendingEmployeeRequests,
                    openOnLoad: Object.hasOwn(
                        this.queryParams,
                        'employeepanel'
                    ),
                    activeTab: Object.hasOwn(this.queryParams, 'employeepanel')
                        ? this.queryParams.employeepanel
                        : null,
                })
            }

            return panels
        },
    },

    methods: {
        reloadAffiliationPanel(id) {
            if (id === 'members') {
                router.reload({
                    only: [
                        'members',
                        'pendingMembershipInvitations',
                        'pendingMembershipRequests',
                    ],
                })
            } else if (id === 'employeepanel') {
                router.reload({
                    only: ['employees', 'pendingEmployeeRequests'],
                })
            }
        },

        requestToJoin() { },
    },
}
</script>
